import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLayoutEffect } from 'react';
import Scrollbar from '../../Scrollbar/Scrollbar';

interface Item {
  pos: number;
}
interface Props<I extends Item> {
  height?: number | string;
  items: I[];
  itemHeight: number;
  focusItem: number;
  onFocusItem(idx: number): void;
  pageSize: number;
  onPageSize(size: number): void;
  totalItems: number;
  itemComponent: React.ComponentType<{ item: I }>;
  preloadFactor?: number;
}

let debug = false;
let slideOffset = debug ? 50 : 0;

export const VirtualScrolling = <I extends Item>(props: Props<I>) => {
  const {
    height,
    items,
    itemHeight,
    focusItem,
    onFocusItem,
    pageSize,
    onPageSize,
    totalItems,
    itemComponent,
    preloadFactor = 3
  } = props;

  const RenderItem = itemComponent;

  const itemRefList = useRef(new Map());
  const containerRef = useRef(null);

  const totalHeight = totalItems * itemHeight || 600;

  const onContainerRef = useCallback((node) => {
    containerRef.current = node;
  }, []);

  const pageRange = useMemo(() => [...Array(pageSize * 2)], [pageSize]);

  const lastUpdate = useRef(0);
  const scrollItemRef = useRef(null);
  const itemAf = useRef(null);

  const focusItemRef = useRef(focusItem);
  focusItemRef.current = focusItem;

  const onScroll = useCallback(
    (ev = undefined, values = undefined) => {
      if (!containerRef.current) {
        return;
      }

      const scrollPos = values
        ? values.scrollTop
        : containerRef.current.scrollTop;

      const scrollHeight = containerRef.current.scrollHeight;
      const viewHeight = containerRef.current.clientHeight;

      const scrollRange = scrollHeight - viewHeight;
      const scrollRatio = scrollPos / scrollRange || 0;
      const correctedPos = scrollRatio * scrollHeight;

      let scrollItem = Math.min(
        Math.floor(correctedPos / itemHeight),
        totalItems - 1
      );
      scrollItem = Math.max(0, scrollItem);

      // console.info('DEBUG scroll', {
      //   scrollHeight,
      //   viewHeight,
      //   scrollRange,
      //   scrollRatio,
      //   correctedPos,
      //   scrollItem,
      //   itemHeight,
      //   totalItems
      // });

      let itemPos = scrollItem * itemHeight;
      let ratio = (correctedPos - itemPos) / itemHeight;

      let itemNode = itemRefList.current.get(scrollItem.toString());

      if (debug && scrollItemRef.current) {
        scrollItemRef.current.style.opacity = 1;
      }

      scrollItemRef.current = itemNode;

      if (debug && scrollItemRef.current) {
        scrollItemRef.current.style.opacity = 0.3;
      }

      if (ev === 'focuspage') {
        if (
          // focusItemRef.current !== scrollItem
          Math.abs(focusItemRef.current - scrollItem) >
          Math.floor(pageSize * 0.25)
        ) {
          onFocusItem(scrollItem);
        }
      }

      if (values) {
        if (!itemAf.current) {
          itemAf.current = setTimeout(() => {
            onScroll('focuspage');

            itemAf.current = null;
          }, 1000 / 30);
        }
      }

      if (!itemNode) {
        // console.warn('main itemNode missing', scrollItem);
        return;
      }

      let s =
        scrollItem * itemHeight - ratio * (itemNode.offsetHeight - itemHeight);

      let slide = (1 - Math.sin(ratio * Math.PI)) * slideOffset;
      itemNode.style.transform = `translate3d(${slide}px,${s}px, 0)`;

      s += itemNode.offsetHeight;

      const transforms = [];

      for (let idx = 0; idx < pageRange.length; idx++) {
        const id = scrollItem + idx + 1;
        itemNode = itemRefList.current.get(id.toString());
        if (itemNode) {
          transforms[idx] = `translate3d(${slideOffset}px,${s}px,0)`;
          s += itemNode.offsetHeight;
        } else {
          // console.warn('itemNode missing', id);
        }
      }

      itemNode = itemRefList.current.get(scrollItem.toString());
      s =
        scrollItem * itemHeight - ratio * (itemNode.offsetHeight - itemHeight);

      for (let idx = 0; idx < pageRange.length; idx++) {
        const id = scrollItem - idx - 1;
        itemNode = itemRefList.current.get(id.toString());
        // console.info('itemNode before', itemNode);
        if (itemNode) {
          s -= itemNode.offsetHeight;
          transforms[
            idx + pageRange.length
          ] = `translate3d(${slideOffset}px,${s}px,0)`;
        } else {
          // console.warn('itemNode missing', id);
        }
      }

      for (let idx = 0; idx < pageRange.length; idx++) {
        const idA = scrollItem + idx + 1;
        const idB = scrollItem - idx - 1;

        itemNode = itemRefList.current.get(idA.toString());
        // console.info('itemNode before', itemNode);
        if (itemNode) {
          itemNode.style.transform = transforms[idx];
        }
        itemNode = itemRefList.current.get(idB.toString());
        if (itemNode) {
          itemNode.style.transform = transforms[idx + pageRange.length];
        }
      }

      lastUpdate.current = performance.now();
    },
    [onFocusItem, totalItems, pageSize, itemHeight, pageRange]
  );

  useEffect(() => onScroll(), [focusItem, pageSize, items, onScroll]);

  const af = useRef(null);
  const onItemRef = useCallback(
    (node) => {
      if (node) {
        // console.dir(node, node.dataset);
        itemRefList.current.set(node.dataset.itemId, node);
        onScroll();
      } else {
        if (af.current) {
          cancelAnimationFrame(af.current);
        }
        af.current = requestAnimationFrame(() => {
          itemRefList.current.forEach((i, k, m) => {
            if (!i.isConnected) {
              m.delete(k);
              itemRefList.current.delete(k);
            }
          });
        });
      }
    },
    [onScroll]
  );

  useEffect(() => {
    onPageSize(
      Math.ceil(containerRef.current.clientHeight / itemHeight) * preloadFactor
    );
  }, [onPageSize, itemHeight, preloadFactor]);

  useLayoutEffect(() => {
    let af;

    const loop = (now) => {
      af = requestAnimationFrame(loop);
      onScroll();
    };
    af = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(af);
  }, [onScroll]);

  return (
    <Scrollbar
      ref={onContainerRef}
      onScrollFrame={onScroll}
      height={height}
      setScrollingBoolean={true}
    >
      <div
        style={{
          height: totalHeight,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {items.map((i, idx) => {
          return (
            <div
              style={{
                background: debug
                  ? i.pos % 2 === 0
                    ? i.pos % 4 === 0
                      ? 'green'
                      : 'red'
                    : 'blue'
                  : 'transparent',
                borderRadius: debug ? (i.pos === focusItem ? 40 : 0) : 'unset',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%'
              }}
              key={i.pos}
              ref={onItemRef}
              data-item-id={i.pos}
            >
              <RenderItem item={i} />
            </div>
          );
        })}
      </div>
    </Scrollbar>
  );
};
